import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  Box, Typography, Stack, Chip, Alert, Button, TextField, IconButton, Tooltip,
  CircularProgress, LinearProgress, InputAdornment, Switch, FormControlLabel,
  Table, TableBody, TableCell, TableHead, TableRow, Tabs, Tab,
} from '@mui/material';
import BrokenImageRounded from '@mui/icons-material/BrokenImageRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import FolderOpenRounded from '@mui/icons-material/FolderOpenRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';

import {
  getArtworkWarmFailures, listArtworkPathOverrides, deleteArtworkPathOverride,
  ArtworkWarmFailure, ArtworkPathOverride,
} from './productionApi';
import { productionKeys } from './queryKeys';
import { TOKENS } from './board/ganttConfig';
import ArtworkPathDialog from './ArtworkPathDialog';
import ArtworkSpecCheckTab from './ArtworkSpecCheckTab';
import { proofScopeUrl, fmtDateTime, fmtDate, apiErr } from './artworkHealthShared';

/**
 * What each `last_error_kind` MEANS, and whose problem it is.
 *
 * This is the whole point of the screen. Before migration 138 every failure
 * looked identical in the logs, so "pre-press hasn't drawn it yet", "our path
 * convention doesn't match where this customer's files live" and "wave went
 * silent" were one undifferentiated pile — and the pile was invisible anyway.
 * `fixable` drives whether the row offers "Fix path": an override is the answer
 * to not_found, and pure noise against a timeout.
 */
const KINDS: Record<string, { label: string; blurb: string; colour: string; fixable: boolean }> = {
  not_found: {
    label: 'No artwork found',
    blurb: 'Cloudflow answered cleanly: nothing at any path we tried. Either pre-press '
      + 'has not drawn it yet, or the file lives somewhere the naming convention does not '
      + 'reach — that second case is what a path override fixes.',
    colour: TOKENS.warn, fixable: true,
  },
  timeout: {
    label: 'Wave timed out',
    blurb: 'Cloudflow accepted the connection then went silent for the full read timeout. '
      + 'A path override will NOT help — this is a wave.abaris.com.au problem, and each one '
      + 'costs the nightly run minutes it does not have.',
    colour: TOKENS.bad, fixable: false,
  },
  transport: {
    label: 'Wave unreachable',
    blurb: 'Connection refused, reset, or DNS failed. Wave was down or unroutable when '
      + 'we tried. Nothing to fix per item.',
    colour: TOKENS.bad, fixable: false,
  },
  cloudflow: {
    label: 'Cloudflow error',
    blurb: 'Cloudflow returned an explicit API error. Worth reading the detail — it is '
      + 'usually permissions or an unmapped store, not the artwork.',
    colour: TOKENS.bad, fixable: true,
  },
  no_preview: {
    label: 'No preview',
    blurb: 'Separations resolved but no thumbnail did. The ink numbers are fine; only '
      + 'the picture is missing.',
    colour: TOKENS.ink3, fixable: true,
  },
  no_path: {
    label: 'No path to try',
    blurb: 'We could not even build a candidate path — a blank customer or stock code on '
      + 'the item. Fix the item in Prism, or point it at a file by hand.',
    colour: TOKENS.ink3, fixable: true,
  },
  config: {
    label: 'Not configured',
    blurb: 'Cloudflow credentials are missing in this environment. Every item will fail '
      + 'until that is set.',
    colour: TOKENS.bad, fixable: false,
  },
};

const kindOf = (k: string | null) => KINDS[k ?? ''] ?? {
  label: k ?? 'Unknown', blurb: 'Unclassified failure — read the detail.',
  colour: TOKENS.ink3, fixable: true,
};

/**
 * ArtworkHealthView — the worklist for artwork the nightly warm can't read.
 *
 * The warm timer has logged its failures to production.artwork_warm_state since
 * migration 138, but nothing read that table, so the only way to see which items
 * were burning the nightly budget was an App Insights query. This is that table
 * as a screen, with the fix attached: every row that a path correction could
 * plausibly help opens the same ArtworkPathDialog the Ink Assign grid uses, and
 * saving there re-reads the file AND clears the item's fail_count — so a fixed
 * item leaves this list immediately rather than waiting for a nightly run that
 * might never reach it.
 *
 * Two tabs, because they are the two halves of one job: Failures is what is
 * broken, Overrides is what has already been corrected (and, when a row appears
 * in both, what was corrected and is broken anyway).
 */
export default function ArtworkHealthView() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'failures' | 'spec' | 'overrides'>('failures');
  const [kind, setKind] = useState<string>('');
  const [search, setSearch] = useState('');
  const [openOnly, setOpenOnly] = useState(false);
  const [fixing, setFixing] = useState<{ customer: string; ref: string; path: string | null; overridden: boolean } | null>(null);

  const params = { kind: kind || undefined, search: search.trim() || undefined, openOnly };
  const healthQ = useQuery({
    queryKey: productionKeys.artworkWarmFailures(params),
    queryFn: () => getArtworkWarmFailures(params),
    staleTime: 60 * 1000,
  });

  const overridesQ = useQuery({
    queryKey: productionKeys.artworkPathOverrides(),
    queryFn: () => listArtworkPathOverrides(),
    staleTime: 60 * 1000,
    enabled: tab === 'overrides',
  });

  const dropOverride = useMutation({
    mutationFn: (o: ArtworkPathOverride) => deleteArtworkPathOverride(o.customer_code, o.customer_ref),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productionKeys.artworkPathOverrides() });
      queryClient.invalidateQueries({ queryKey: ['production', 'artwork-warm-failures'] });
      queryClient.invalidateQueries({ queryKey: ['production', 'artwork-spec-check'] });
    },
  });

  // A re-pull changes the separations, so it changes everything downstream of
  // them — and it clears this item's fail_count server-side, so the list this
  // row came from is stale the moment the dialog saves.
  const onArtworkSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['production', 'artwork-warm-failures'] });
    queryClient.invalidateQueries({ queryKey: ['production', 'artwork-spec-check'] });
    queryClient.invalidateQueries({ queryKey: productionKeys.artworkPathOverrides() });
    queryClient.invalidateQueries({ queryKey: ['production', 'ink-assignment'] });
    queryClient.invalidateQueries({ queryKey: ['production', 'ink-sticker'] });
    queryClient.invalidateQueries({ queryKey: productionKeys.inkDemandSummary() });
  };

  const data = healthQ.data;
  const byKind = useMemo(
    () => [...(data?.summary.by_kind ?? [])].sort((a, b) => b.items - a.items),
    [data],
  );
  const activeKind = kind ? kindOf(kind) : null;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.5 }}>
        <BrokenImageRounded sx={{ color: TOKENS.accent }} />
        <Typography sx={{ fontWeight: 700, fontSize: 20 }}>Artwork health</Typography>
        <Tooltip title="Re-read the worklist">
          <IconButton size="small" onClick={() => healthQ.refetch()}>
            <RefreshRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Typography sx={{ fontSize: 13, color: TOKENS.ink3, mb: 1.5 }}>
        Is the artwork we resolve for each job the right file? Failures are the items we
        cannot read at all; Spec vs artwork is the files we can read that disagree with
        Prism's ink spec. Fixing a path re-reads the file straight away and re-checks both.
      </Typography>

      {/* ---- Run status ------------------------------------------------------- */}
      {data?.warm && (
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap
          sx={{ mb: 1.5, fontSize: 12.5, color: TOKENS.ink2 }}>
          <span>Last successful warm: <strong>{fmtDateTime(data.warm.last_success_at)}</strong></span>
          <span>
            {data.summary.ok.toLocaleString()} of {data.summary.tracked.toLocaleString()} tracked
            items warm
          </span>
          {data.summary.never_ok > 0 && (
            <span>{data.summary.never_ok.toLocaleString()} have never warmed</span>
          )}
          {data.warm.error_count > 0 && (
            <Chip size="small" label={`${data.warm.error_count} consecutive run errors`}
              sx={{ height: 20, fontSize: 11, fontWeight: 700, color: TOKENS.bad, bgcolor: `${TOKENS.bad}18` }} />
          )}
        </Stack>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1.5, minHeight: 36 }}>
        <Tab value="failures" label={`Failures${data ? ` (${data.summary.total})` : ''}`}
          sx={{ minHeight: 36, fontSize: 13, textTransform: 'none', fontWeight: 600 }} />
        <Tab value="spec" label="Spec vs artwork"
          sx={{ minHeight: 36, fontSize: 13, textTransform: 'none', fontWeight: 600 }} />
        <Tab value="overrides" label="Path overrides"
          sx={{ minHeight: 36, fontSize: 13, textTransform: 'none', fontWeight: 600 }} />
      </Tabs>

      {tab === 'failures' && (
        <>
          {/* ---- Reason chips: click to filter -------------------------------- */}
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
            <Chip size="small" label={`All · ${data?.summary.total ?? 0}`}
              onClick={() => setKind('')}
              variant={kind ? 'outlined' : 'filled'}
              sx={{
                fontSize: 11.5, fontWeight: 700,
                ...(kind ? {} : { color: '#fff', bgcolor: TOKENS.ink }),
              }} />
            {byKind.map((k) => {
              const meta = kindOf(k.last_error_kind);
              const on = kind === k.last_error_kind;
              return (
                <Tooltip key={k.last_error_kind ?? 'null'} title={meta.blurb}>
                  <Chip size="small" label={`${meta.label} · ${k.items}`}
                    onClick={() => setKind(on ? '' : (k.last_error_kind ?? ''))}
                    variant={on ? 'filled' : 'outlined'}
                    sx={{
                      fontSize: 11.5, fontWeight: 700,
                      color: on ? '#fff' : meta.colour,
                      bgcolor: on ? meta.colour : 'transparent',
                      borderColor: `${meta.colour}66`,
                    }} />
                </Tooltip>
              );
            })}
          </Stack>

          {activeKind && (
            <Alert severity="info" sx={{ mb: 1.25, py: 0.25, fontSize: 12.5 }}>
              <strong>{activeKind.label}</strong> — {activeKind.blurb}
            </Alert>
          )}

          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.25 }}>
            <TextField
              size="small" sx={{ minWidth: 300 }}
              placeholder="Filter by client, item or description…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchRounded sx={{ fontSize: 16 }} /></InputAdornment>
                ),
                sx: { fontSize: 12.5 },
              }}
            />
            <FormControlLabel
              control={<Switch size="small" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />}
              label={
                <Typography sx={{ fontSize: 12.5, color: TOKENS.ink2 }}>
                  Open jobs only{data ? ` (${data.summary.open_jobs})` : ''}
                </Typography>
              } />
            {(data?.summary.with_override ?? 0) > 0 && (
              <Tooltip title="Someone already pointed these at a corrected path and they are STILL failing — a different problem from the rest of the list.">
                <Chip size="small" label={`✎ ${data?.summary.with_override} failing despite an override`}
                  sx={{
                    height: 20, fontSize: 11, fontWeight: 700,
                    color: TOKENS.accent, bgcolor: `${TOKENS.accent}1f`,
                    border: `1px solid ${TOKENS.accent}55`,
                  }} />
              </Tooltip>
            )}
          </Stack>

          {healthQ.isFetching && <LinearProgress sx={{ height: 2, mb: 1 }} />}
          {healthQ.isError && (
            <Alert severity="error" sx={{ fontSize: 12.5 }}>Couldn’t load: {apiErr(healthQ.error)}</Alert>
          )}
          {healthQ.isLoading && (
            <Stack alignItems="center" sx={{ py: 6 }} spacing={1.5}>
              <CircularProgress size={28} />
            </Stack>
          )}

          {data && !data.failures.length && !healthQ.isLoading && (
            <Stack alignItems="center" sx={{ py: 8 }} spacing={1}>
              <BrokenImageRounded sx={{ fontSize: 40, color: TOKENS.good, opacity: 0.5 }} />
              <Typography sx={{ fontSize: 14, color: TOKENS.ink2, fontWeight: 600 }}>
                {data.summary.total ? 'Nothing matches those filters.' : 'Every warmed item read cleanly.'}
              </Typography>
            </Stack>
          )}

          {!!data?.failures.length && (
            <>
              <FailureTable rows={data.failures}
                onFix={(r) => setFixing({
                  customer: r.customer_code, ref: r.stock_code,
                  path: r.override_path, overridden: r.has_path_override,
                })} />
              {data.returned >= data.limit && (
                <Typography sx={{ fontSize: 11.5, color: TOKENS.ink3, mt: 1 }}>
                  Showing the first {data.limit} of {data.summary.total} — narrow the filters to
                  reach the rest.
                </Typography>
              )}
            </>
          )}
        </>
      )}

      {tab === 'spec' && (
        <ArtworkSpecCheckTab
          onFix={(r) => setFixing({
            customer: r.customer_code, ref: r.customer_ref,
            path: r.artwork_path, overridden: r.artwork_path_overridden,
          })} />
      )}

      {tab === 'overrides' && (
        <OverrideTable
          rows={overridesQ.data ?? []}
          loading={overridesQ.isLoading}
          error={overridesQ.isError ? apiErr(overridesQ.error) : null}
          onEdit={(o) => setFixing({
            customer: o.customer_code, ref: o.customer_ref,
            path: o.pdf_path, overridden: true,
          })}
          onDelete={(o) => dropOverride.mutate(o)}
          deleting={dropOverride.isPending}
        />
      )}

      {fixing && (
        <ArtworkPathDialog
          open
          customerCode={fixing.customer}
          customerRef={fixing.ref}
          currentPath={fixing.path}
          overridden={fixing.overridden}
          onClose={() => setFixing(null)}
          onSaved={onArtworkSaved}
        />
      )}
    </Box>
  );
}

const HEAD = { fontWeight: 700, fontSize: 11, color: TOKENS.ink3, py: 0.5 } as const;
const CELL = { fontSize: 12, py: 0.6 } as const;

function FailureTable({ rows, onFix }: {
  rows: ArtworkWarmFailure[];
  onFix: (r: ArtworkWarmFailure) => void;
}) {
  return (
    <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px', overflowX: 'auto' }}>
      <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)', px: 1 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...HEAD, width: 78 }}>Client</TableCell>
            <TableCell sx={{ ...HEAD, width: 150 }}>Item</TableCell>
            <TableCell sx={HEAD}>Description</TableCell>
            <TableCell sx={{ ...HEAD, width: 140 }}>Why it failed</TableCell>
            <TableCell sx={{ ...HEAD, width: 56, textAlign: 'right' }}>Fails</TableCell>
            <TableCell sx={{ ...HEAD, width: 96 }}>Next due</TableCell>
            <TableCell sx={{ ...HEAD, width: 108 }}>Last tried</TableCell>
            <TableCell sx={{ ...HEAD, width: 100 }}>Last warm</TableCell>
            <TableCell sx={{ ...HEAD, width: 92 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => {
            const meta = kindOf(r.last_error_kind);
            return (
              <TableRow key={`${r.customer_code}/${r.stock_code}`} hover>
                <TableCell sx={{ ...CELL, fontWeight: 700 }}>{r.customer_code}</TableCell>
                <TableCell sx={{ ...CELL, fontFamily: TOKENS.monoFont, wordBreak: 'break-all' }}>
                  {r.stock_code}
                  {r.has_path_override && (
                    <Tooltip title={`Already pointed at ${r.override_path} — and still failing.`}>
                      <span style={{ color: TOKENS.accent, fontWeight: 700, marginLeft: 4 }}>✎</span>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell sx={{ ...CELL, color: TOKENS.ink2 }}>
                  {r.description ?? <span style={{ color: TOKENS.ink3 }}>—</span>}
                  {r.is_open_job && (
                    <Chip size="small" label="open job"
                      sx={{
                        ml: 0.75, height: 16, fontSize: 9.5, fontWeight: 700,
                        color: TOKENS.accent, bgcolor: `${TOKENS.accent}1a`,
                      }} />
                  )}
                </TableCell>
                <TableCell sx={CELL}>
                  <Tooltip title={r.last_error || meta.blurb}>
                    <Chip size="small" label={meta.label}
                      sx={{
                        height: 19, fontSize: 10.5, fontWeight: 700,
                        color: meta.colour, bgcolor: `${meta.colour}1a`,
                      }} />
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ ...CELL, textAlign: 'right', fontWeight: 700 }}>{r.fail_count}</TableCell>
                <TableCell sx={{ ...CELL, color: TOKENS.ink2 }}>{fmtDate(r.next_due)}</TableCell>
                <TableCell sx={{ ...CELL, color: TOKENS.ink3 }}>{fmtDateTime(r.last_attempt_at)}</TableCell>
                <TableCell sx={{ ...CELL, color: TOKENS.ink3 }}>
                  {/* "Never" vs a date is the single most useful distinction in
                      this table: never = the artwork has never existed where we
                      look; a date = it did, and then moved. */}
                  {r.last_ok_at
                    ? fmtDate(r.last_ok_at)
                    : <span style={{ color: TOKENS.bad, fontWeight: 700 }}>never</span>}
                </TableCell>
                <TableCell sx={{ ...CELL, textAlign: 'right' }}>
                  <Tooltip title={meta.fixable
                    ? 'Browse Cloudflow, pick the right PDF, and re-read it now'
                    : 'A path override will not help this failure — but you can still look'}>
                    <Button size="small" startIcon={<FolderOpenRounded sx={{ fontSize: 15 }} />}
                      onClick={() => onFix(r)}
                      sx={{
                        fontSize: 11, textTransform: 'none', minWidth: 0, px: 0.75,
                        color: meta.fixable ? TOKENS.accent : TOKENS.ink3,
                      }}>
                      Fix path
                    </Button>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

function OverrideTable({ rows, loading, error, onEdit, onDelete, deleting }: {
  rows: ArtworkPathOverride[];
  loading: boolean;
  error: string | null;
  onEdit: (o: ArtworkPathOverride) => void;
  onDelete: (o: ArtworkPathOverride) => void;
  deleting: boolean;
}) {
  if (error) return <Alert severity="error" sx={{ fontSize: 12.5 }}>Couldn’t load: {error}</Alert>;
  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={28} /></Stack>
    );
  }
  if (!rows.length) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }} spacing={1}>
        <FolderOpenRounded sx={{ fontSize: 40, color: TOKENS.ink3, opacity: 0.5 }} />
        <Typography sx={{ fontSize: 14, color: TOKENS.ink2, fontWeight: 600 }}>
          No path overrides saved.
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: TOKENS.ink3 }}>
          Every item resolves through the standard naming convention.
        </Typography>
      </Stack>
    );
  }
  return (
    <>
      <Typography sx={{ fontSize: 12.5, color: TOKENS.ink3, mb: 1 }}>
        Items pointed at a hand-picked Cloudflow file instead of the naming convention.
        Removing one sends that item back to the convention — and back onto the failures
        list if the convention still can’t find it.
      </Typography>
      <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px', overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)', px: 1 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...HEAD, width: 78 }}>Client</TableCell>
              <TableCell sx={{ ...HEAD, width: 150 }}>Item</TableCell>
              <TableCell sx={HEAD}>Cloudflow path</TableCell>
              <TableCell sx={{ ...HEAD, width: 150 }}>Note</TableCell>
              <TableCell sx={{ ...HEAD, width: 108 }}>Updated</TableCell>
              <TableCell sx={{ ...HEAD, width: 108 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((o) => (
              <TableRow key={`${o.customer_code}/${o.customer_ref}`} hover>
                <TableCell sx={{ ...CELL, fontWeight: 700 }}>{o.customer_code}</TableCell>
                <TableCell sx={{ ...CELL, fontFamily: TOKENS.monoFont, wordBreak: 'break-all' }}>
                  {o.customer_ref}
                </TableCell>
                <TableCell sx={{ ...CELL, fontFamily: TOKENS.monoFont, fontSize: 11, color: TOKENS.ink2, wordBreak: 'break-all' }}>
                  {o.pdf_path}
                  <Tooltip title="Open in ProofScope">
                    <IconButton size="small" component="a" target="_blank" rel="noreferrer"
                      href={proofScopeUrl(o.pdf_path)} sx={{ ml: 0.25 }}>
                      <OpenInNewRounded sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ ...CELL, color: TOKENS.ink2 }}>{o.note ?? '—'}</TableCell>
                <TableCell sx={{ ...CELL, color: TOKENS.ink3 }}>{fmtDateTime(o.updated_at)}</TableCell>
                <TableCell sx={{ ...CELL, textAlign: 'right' }}>
                  <Tooltip title="Re-point or re-read this item">
                    <IconButton size="small" onClick={() => onEdit(o)} sx={{ color: TOKENS.accent }}>
                      <FolderOpenRounded sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove the override — back to the naming convention">
                    <span>
                      <IconButton size="small" disabled={deleting} onClick={() => onDelete(o)}
                        sx={{ color: TOKENS.ink3 }}>
                        <DeleteOutlineRounded sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </>
  );
}
